import { StoreCustomerListParams } from "$/lib/queries/store-customer-queries"
import {
  PriceUtils,
  StoreCustomerListItem,
  StoreCustomerSortBy,
} from "@repo/common"
import { DataTableColumnHeader } from "@repo/ui/components/data-table"
import { JuloBadge } from "@repo/ui/components/julo-badge"
import { Button } from "@repo/ui/components/ui/button"
import { ColumnDef, SortingState } from "@tanstack/react-table"
import { Locale, format } from "date-fns"
import { MessageSquare } from "lucide-react"

export const STORE_CUSTOMER_PAGE_SIZE = 20

export const DEFAULT_STORE_CUSTOMER_LIST_PARAMS: StoreCustomerListParams = {
  page: 1,
  size: STORE_CUSTOMER_PAGE_SIZE,
  sortBy: StoreCustomerSortBy.LATEST_JOB_DATE,
  sortOrder: "desc",
}

// Column ids double as the API's sort keys.
const SORTABLE_COLUMNS: StoreCustomerSortBy[] = [
  StoreCustomerSortBy.NAME,
  StoreCustomerSortBy.NUM_JOBS,
  StoreCustomerSortBy.LATEST_JOB_DATE,
  StoreCustomerSortBy.TOTAL_SPENT,
]

export const toStoreCustomerSort = (
  sorting: SortingState,
): Pick<StoreCustomerListParams, "sortBy" | "sortOrder"> => {
  const [first] = sorting
  const sortBy = SORTABLE_COLUMNS.find((column) => column === first?.id)
  if (!first || !sortBy) {
    return {
      sortBy: DEFAULT_STORE_CUSTOMER_LIST_PARAMS.sortBy,
      sortOrder: DEFAULT_STORE_CUSTOMER_LIST_PARAMS.sortOrder,
    }
  }
  return { sortBy, sortOrder: first.desc ? "desc" : "asc" }
}

const formatDate = (date: Date | null, locale: Locale) =>
  date ? format(new Date(date), "d MMM yyyy", { locale }) : "—"

type StoreCustomerColumnLabel =
  | "name"
  | "customerClub"
  | "phoneNumber"
  | "email"
  | "jobs"
  | "latestJob"
  | "total"
  | "actions"
  | "sendMessage"

interface GetStoreCustomerColumnsOptions {
  onSendMessage: (customer: StoreCustomerListItem) => void
  t: (key: StoreCustomerColumnLabel) => string
  locale: Locale
}

export function getStoreCustomerColumns({
  onSendMessage,
  t,
  locale,
}: GetStoreCustomerColumnsOptions): ColumnDef<StoreCustomerListItem>[] {
  return [
    {
      id: StoreCustomerSortBy.NAME,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="line-clamp-1">
            {[row.original.firstName, row.original.lastName]
              .filter(Boolean)
              .join(" ")}
          </span>
          {row.original.isClubMember && (
            <JuloBadge>{t("customerClub")}</JuloBadge>
          )}
        </div>
      ),
      enableSorting: true,
      meta: { label: t("name") },
    },
    {
      accessorKey: "phoneNumber",
      header: t("phoneNumber"),
      enableSorting: false,
      meta: { label: t("phoneNumber") },
    },
    {
      id: "email",
      header: t("email"),
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.original.email ?? "—"}</span>
      ),
      enableSorting: false,
      meta: { label: t("email") },
    },
    {
      id: StoreCustomerSortBy.NUM_JOBS,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("jobs")} />
      ),
      cell: ({ row }) => row.original.numJobs,
      enableSorting: true,
      meta: { label: t("jobs") },
    },
    {
      id: StoreCustomerSortBy.LATEST_JOB_DATE,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("latestJob")} />
      ),
      cell: ({ row }) => formatDate(row.original.latestJobDate, locale),
      enableSorting: true,
      meta: { label: t("latestJob") },
    },
    {
      id: StoreCustomerSortBy.TOTAL_SPENT,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("total")} />
      ),
      cell: ({ row }) => PriceUtils.format(row.original.totalSpent),
      enableSorting: true,
      meta: { label: t("total") },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">{t("actions")}</span>,
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={t("sendMessage")}
          onClick={(event) => {
            event.stopPropagation()
            onSendMessage(row.original)
          }}
        >
          <MessageSquare className="size-4" />
        </Button>
      ),
      enableSorting: false,
    },
  ]
}
